import styled, { css } from 'styled-components';

const converter = {
  s: '200px',
  m: '315px',
  l: '430px',
  full: '100%',
};

const SelectContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['readonly', 'isSearchType', 'zIndexMenu'].includes(prop),
})`
  position: relative;
  width: 100%;
  font-size: 18px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: normal;
  color: ${(props) => props.theme.colors.white};

  .mandatory-select div[class*='-placeholder'] {
    max-width: fit-content;
    position: relative;
    padding-right: 10px;
    &::after {
      content: '*';
      color: #cc334d;
      position: absolute;
      z-index: 1;
      top: -2px;
      right: 0px;
    }
  }

  ${({ size }) =>
    Number.isInteger(size)
      ? css`
          width: ${size}px;
          max-width: ${size}px;
        `
      : Object.keys(converter).includes(size)
      ? css`
          width: ${converter[size]};
          max-width: ${converter[size]};
        `
      : size === 'compact'
      ? css`
          width: unset;
          max-width: fit-content;
          height: 27px;
          min-height: 27px;

          div[class*='-control'] {
            height: 27px;
            min-height: 27px;
            align-items: flex-start;
          }

          div[class*='-indicatorContainer'] {
            padding: 0px 4px;
          }

          div[class*='-placeholder'],
          div[class*='-singleValue'] {
            font-size: 12px !important;
          }

          div[class*='ValueContainer'] {
            padding-block: 0;
          }
        `
      : null}

  @media ${(props) => props.theme.device.mobileL} {
    font-size: 12px;
  }

  div[class*='-placeholder'] {
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  div[class*='-menu'] {
    min-width: fit-content;
    width: fit-content;
    min-width: 100%;
    font-weight: normal;
    z-index: ${({ zIndexMenu }) => zIndexMenu || 3};

    div[class*='-MenuList'] div[class*='-option'] {
      span {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    @media ${(props) => props.theme.device.mobileL} {
      width: 100%;
    }
  }

  div[class*='-indicatorContainer'] {
    position: relative;
    color: ${(props) => props.theme.colors.primaryDarkGray600};
    padding: 0 8px;

    &:first-child svg {
      display: none;
    }
    &:first-child::before {
      cursor: pointer;
      content: '×';
      color: ${(props) => props.theme.colors.primaryDarkGray600};
      font-size: 24px;
      font-weight: 100;
      padding-right: 3px;
      padding-bottom: 2px;
    }

    &:hover {
      color: ${(props) => props.theme.colors.focusSelect};
    }

    &:first-child {
      padding-right: 0;
    }

    &:after {
      content: '';
      display: block;
      height: 70%;
      position: absolute;
      width: 0px;
      background: ${(props) => props.theme.colors.bgSecondary};
      top: 15%;
    }
  }

  div[class*='ValueContainer'] {
    overflow: auto;
    height: inherit;
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: ${(props) => props.theme.colors.focusSelect};

    div[class*='Input'] {
      color: ${(props) => props.theme.colors.textPrimary};
    }
  }

  div[class*='singleValue'] {
    color: ${(props) => props.theme.colors.focusSelect} !important;
  }

  div[class*='multiValue'] {
    color: ${(props) => props.theme.colors.focusSelect} !important;
    background-color: transparent !important;
  }

  div[class*='MultiValueRemove'] {
    color: ${(props) => props.theme.colors.link} !important;

    &:hover {
      color: ${(props) => props.theme.colors.focusSelect} !important;
      background-color: transparent !important;
    }
  }

  div[class*='option'] {
    color: ${(props) => props.theme.colors.title};
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 28px;
    min-width: max-content;
    width: 100%;

    &[class*='disabled'] {
      color: ${(props) => props.theme.colors.placeholder} !important;

      &:active {
        text-decoration: none;
      }
    }

    & .icon-wrapper {
      > svg {
        fill: ${(props) => props.theme.colors.title} !important;
      }
    }
  }

  div[class*='singleValue'] {
    color: ${(props) => props.theme.colors.textSecondary};
  }

  div[class*='control'] {
    > div:first-child {
      overflow: auto;
      height: inherit;
      scrollbar-width: none; //hides the scrollbar without disabling scroll
    }
  }

  ${(props) =>
    props.readonly &&
    css`
      pointer-events: none;

      div[class*='control'] {
        border: none;
        background-color: transparent;
      }

      div[class*='ValueContainer'] {
        padding: 0;
      }

      div[class*='IndicatorsContainer'] {
        display: none;
      }
    `}

  ${({ isSearchType }) =>
    isSearchType &&
    css`
      div[class*='control'] {
        cursor: auto;
      }

      div[class*='-indicatorContainer'] {
        cursor: pointer;
      }

      span ~ div[class*='-indicatorContainer'] {
        display: none;
      }
    `}

    & .has-icon {
    & .icon {
      font-size: 20px;
    }

    display: flex;
    align-items: center;
    gap: 10px;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      div[class*='control'] {
        border-color: #646464 !important;
      }

      /* repeated selector to artificially increase specificity */
      div[class*='singleValue'][class*='singleValue'],
      div[class*='indicatorContainer'],
      div[class*='-placeholder'] {
        color: #646464 !important;
      }
    `}
`;

export { SelectContainer };
